import axios from 'axios';
import { tauriAxiosAdapter } from './tauriAxiosAdapter';

/**
 * Link unfurling for chat. Fetches a URL's HTML and extracts Open Graph / meta
 * tags into a preview card model. This is opt-in (see the `linkPreviews`
 * preference) because the fetch happens directly from the user's device.
 *
 * We deliberately do NOT use `apiClient`: that instance carries the user's auth
 * token and an api.apiboo.com baseURL, neither of which must ever reach a
 * third-party site. This bare instance routes through the same Rust HTTP path
 * (no CORS in the desktop app) but sends no credentials.
 */
const previewClient = axios.create({
  adapter: tauriAxiosAdapter,
  timeout: 8000,
  responseType: 'text',
  // Cap the download so a huge/streaming page can't blow up memory; OG tags
  // live in <head> and arrive well within this.
  maxContentLength: 2 * 1024 * 1024,
  headers: {
    Accept: 'text/html,application/xhtml+xml',
    // Some sites only emit OG tags to a browser-like agent.
    'User-Agent':
      'Mozilla/5.0 (compatible; ApibooChat/1.0; +https://apiboo.com)',
  },
});

// url -> Promise<preview|null>. Caches both in-flight and settled lookups so a
// link repeated across many messages is fetched exactly once per session.
const cache = new Map();
// url -> settled preview|null. Readable synchronously so a remounting message
// row (every channel switch remounts them all) can render its card in the
// first paint instead of popping it in a tick later — that pop-in is a layout
// shift that breaks the list's bottom anchoring.
const resolvedCache = new Map();
const MAX_CACHE = 200;

function isHttpUrl(url) {
  return typeof url === 'string' && /^https?:\/\//i.test(url);
}

function metaContent(doc, selectors) {
  for (const sel of selectors) {
    const el = doc.querySelector(sel);
    const val = el?.getAttribute('content')?.trim();
    if (val) return val;
  }
  return '';
}

function absolutize(maybeUrl, base) {
  if (!maybeUrl) return '';
  try {
    return new URL(maybeUrl, base).href;
  } catch {
    return '';
  }
}

function parsePreview(html, url) {
  // Guard against a non-HTML body (e.g. a direct image/JSON response).
  if (typeof html !== 'string' || !/<\s*(html|head|meta|title)/i.test(html)) {
    return null;
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const title =
    metaContent(doc, ['meta[property="og:title"]', 'meta[name="twitter:title"]']) ||
    doc.querySelector('title')?.textContent?.trim() ||
    '';
  const description = metaContent(doc, [
    'meta[property="og:description"]',
    'meta[name="twitter:description"]',
    'meta[name="description"]',
  ]);
  const rawImage = metaContent(doc, [
    'meta[property="og:image"]',
    'meta[name="twitter:image"]',
    'meta[property="og:image:url"]',
  ]);
  const image = absolutize(rawImage, url);
  const siteName =
    metaContent(doc, ['meta[property="og:site_name"]']) ||
    (() => {
      try {
        return new URL(url).hostname.replace(/^www\./, '');
      } catch {
        return '';
      }
    })();

  // Nothing worth showing.
  if (!title && !description && !image) return null;
  return { url, title, description, image, siteName };
}

/**
 * Fetch and parse a link preview. Returns a preview model or null (invalid URL,
 * fetch failure, non-HTML, or no useful metadata). Never throws.
 * @param {string} url
 * @returns {Promise<{url,title,description,image,siteName}|null>}
 */
export function fetchLinkPreview(url) {
  if (!isHttpUrl(url)) return Promise.resolve(null);
  if (cache.has(url)) return cache.get(url);

  const promise = previewClient
    .get(url)
    .then((res) => parsePreview(res.data, url))
    .catch(() => null)
    .then((preview) => {
      // Settled value (including null = "nothing to show") becomes readable
      // synchronously; a known-null URL must not re-show a loading placeholder.
      if (resolvedCache.size >= MAX_CACHE) {
        resolvedCache.delete(resolvedCache.keys().next().value);
      }
      resolvedCache.set(url, preview);
      return preview;
    });

  // Bound the cache with simple FIFO eviction.
  if (cache.size >= MAX_CACHE) {
    const oldest = cache.keys().next().value;
    cache.delete(oldest);
  }
  cache.set(url, promise);
  return promise;
}

/**
 * Synchronous cache read: the settled preview model, `null` when the URL is
 * known to have no useful preview, or `undefined` when it hasn't settled yet
 * (unknown / still in flight).
 */
export function getCachedLinkPreview(url) {
  return resolvedCache.has(url) ? resolvedCache.get(url) : undefined;
}
